import { nextTick, Ref, ref, UnwrapRef, useRouter } from '@nuxtjs/composition-api';

interface DefaultValuesIndex<T> {
  [key: string]: T;
}

const defaultValuesIndex: DefaultValuesIndex<unknown> = {};

// Plain Map, not reactive — avoids Vue 2.7 auto-unwrapping refs stored inside
// reactive arrays, which caused .value to return undefined.
const pageRefs = new Map<string, Ref<unknown>>();

const path = ref('');
const hasReset = ref(false);

/*
  Ref that resets back to default value when you leave the page.

  Useful for state that needs to be shared between components but should not persist
  between route (path) changes.

  Requires a unique key to prevent duplication

  @example
  ```ts
  const useProductValidation = () => {
    const validation = pageRef([], 'myComposable-validation')
    return {

      validation
    }
  }
  ```
 */
const pageRef = <T>(val: T, key: string): Ref<UnwrapRef<T>> => {
  const router = useRouter();

  const resetState = () => {
    pageRefs.forEach((storedRef, storedKey) => {
      storedRef.value = defaultValuesIndex[storedKey] as UnwrapRef<unknown>;
    });
  };

  // @ts-ignore
  router.beforeEach((to, _, next) => {
    if (path.value !== to.path && hasReset.value === false) {
      nextTick(resetState);
    }
    path.value = to.params.slug;
    hasReset.value = true;
    if (next) next();
  });

  // @ts-ignore
  router.afterEach((_, __, next) => {
    hasReset.value = false;
    if (next) next();
  });

  const existingRef = pageRefs.get(key);
  if (existingRef) {
    return existingRef as unknown as Ref<UnwrapRef<T>>;
  }
  const newRef = ref<T>(val);
  pageRefs.set(key, newRef);
  defaultValuesIndex[key] = val;

  return newRef;
};

export default pageRef;
